import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getJobMatches, submitMatchFeedback } from '../services/api';
import CandidateProfileModal from './CandidateProfileModal';
import JobDetailsModal from './JobDetailsModal';
import './Matchmaker.css';

function Matchmaker() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [sortBy, setSortBy] = useState('match_score');
  const [thumbsDownModal, setThumbsDownModal] = useState({ open: false, matchId: null });
  const [thumbsDownReason, setThumbsDownReason] = useState('');

  const getSortedMatches = () => {
    if (!matchData?.matches || matchData.matches.length === 0) return [];

    const sortedMatches = [...matchData.matches];

    switch (sortBy) {
      case 'match_score':
        return sortedMatches.sort((a, b) => (b.pearch_score || 0) - (a.pearch_score || 0));
      case 'custom_score':
        return sortedMatches.sort((a, b) => (b.custom_score || 0) - (a.custom_score || 0));
      case 'experience':
        return sortedMatches.sort((a, b) => (b.candidate?.total_experience_years || 0) - (a.candidate?.total_experience_years || 0));
      case 'name':
        return sortedMatches.sort((a, b) => (a.candidate?.full_name || '').localeCompare(b.candidate?.full_name || ''));
      default:
        return sortedMatches;
    }
  };

  useEffect(() => {
    fetchMatches();
  }, [jobId]);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      const data = await getJobMatches(jobId);
      const normalizedData = {
        ...data,
        matches: (data.matches || []).map((m) => ({
          ...m,
          feedback: m.feedback === 'none' ? null : m.feedback,
        })),
      };
      setMatchData(normalizedData);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (match) => {
    setSelectedMatch(match);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMatch(null);
  };

  const handleFeedback = async (matchId, feedbackType, notes = '') => {
    try {
      await submitMatchFeedback(matchId, feedbackType, notes);
      setMatchData(prevData => ({
        ...prevData,
        matches: prevData.matches.map(m =>
          m.id === matchId ? { ...m, feedback: feedbackType } : m
        )
      }));
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleResetFeedback = async (matchId) => {
    try {
      await submitMatchFeedback(matchId, null, '');
      // Only clear local state after the backend confirms the reset
      setMatchData(prevData => ({
        ...prevData,
        matches: prevData.matches.map(m =>
          m.id === matchId ? { ...m, feedback: null } : m
        )
      }));
    } catch (err) {
      console.error('Error resetting feedback:', err);
    }
  };

  const handleThumbsDown = (matchId) => {
    setThumbsDownReason('');
    setThumbsDownModal({ open: true, matchId });
  };

  const handleThumbsDownConfirm = async () => {
    await handleFeedback(thumbsDownModal.matchId, 'thumbs_down', thumbsDownReason);
    setThumbsDownModal({ open: false, matchId: null });
    setThumbsDownReason('');
  };

  const handleThumbsDownSkip = async () => {
    await handleFeedback(thumbsDownModal.matchId, 'thumbs_down', '');
    setThumbsDownModal({ open: false, matchId: null });
    setThumbsDownReason('');
  };

  const getMatchPercentageClass = (percentage) => {
    if (percentage >= 90) return 'high';
    if (percentage >= 80) return 'medium';
    return 'low';
  };

  const formatMatchDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);

    // Format: "Matched Jan 30, 2026 at 2:45 PM"
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return `Matched ${dateStr} at ${timeStr}`;
  };

  if (loading) {
    return (
      <div className="matchmaker-container">
        <div className="loading-state">Loading matches...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="matchmaker-container">
        <div className="error-state">
          <p>Error: {error}</p>
          <button onClick={() => navigate('/matchmaker')} className="btn-back">
            Back to Matchmaker
          </button>
        </div>
      </div>
    );
  }

  if (!matchData) {
    return null;
  }

  const { job, total_pool, top_score } = matchData;

  return (
    <div className="matchmaker-container">
      {/* Header Section */}
      <div className="matchmaker-header">
        <div className="header-top">
          <button onClick={() => navigate('/matchmaker')} className="btn-back-arrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
            </svg>
            Back
          </button>
          <div className="header-title-section">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#8b5cf6" />
              </svg>
            </div>
            <div>
              <h1 className="job-title">Matchmaker - Job Matches</h1>
              <p className="job-subtitle">Ranked candidate shortlist with AI-powered scoring</p>
            </div>
          </div>
        </div>

        <div className="job-details-card">
          <div className="job-info">
            <div className="job-title-row" onClick={() => setShowJobModal(true)}>
              <h2 className="job-name">{job.title}</h2>
              <button className="btn-show-details">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#6b7280" />
                </svg>
                Show Details
              </button>
            </div>
            <div className="job-meta">
              <span className="company-name">{job.company}</span>
              <span className="separator">•</span>
              <span className="location">{job.location}</span>
              {job.practice_area && (
                <>
                  <span className="separator">•</span>
                  <span className="practice-area">{job.practice_area}</span>
                </>
              )}
            </div>
          </div>

          <div className="job-stats">
            <div className="stat-item">
              <div className="stat-label">TOTAL POOL</div>
              <div className="stat-value">{total_pool || 0}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">TOP SCORE</div>
              <div className="stat-value">{top_score * 20 || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Matches Section */}
      <div className="matches-section">
        <div className="matches-header">
          <div className="matches-title">
            <h3>Top Matches</h3>
            <p className="matches-subtitle">AI-powered candidate ranking with explainable scoring</p>
          </div>
          <div className="matches-controls">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="match_score">Sort by: Pearch Score</option>
              <option value="custom_score">Sort by: Custom Score</option>
              <option value="experience">Sort by: Experience</option>
              <option value="name">Sort by: Name</option>
            </select>
          </div>
        </div>

        {/* Match Cards */}
        <div className="matches-grid">
          {getSortedMatches().length === 0 ? (
            <div className="no-matches">
              <p>No matches found for this job.</p>
            </div>
          ) : (
            getSortedMatches().map((match) => (
              <div key={match.id} className={`match-card${match.feedback === 'thumbs_down' ? ' match-card--rejected' : ''}`}>
                <div className="match-card-header">
                  <div className="candidate-info">
                    <div className="candidate-photo-wrapper">
                      <div className="candidate-photo-placeholder">
                        {match.candidate?.first_name?.[0] || '?'}
                      </div>
                      {match.candidate?.picture_url && (
                        <img
                          src={match.candidate.picture_url}
                          alt={match.candidate.full_name}
                          className="candidate-photo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                    <div className="candidate-details">
                      <div className="candidate-name-row">
                        <h4 className="candidate-name">{match.candidate?.full_name || 'Unknown'}</h4>
                      </div>
                      <div className="candidate-meta">
                        <span>{match.candidate?.total_experience_years || 0} years experience</span>
                        {match.candidate?.educations?.[0] && (
                          <>
                            <span className="separator">•</span>
                            <span>{match.candidate.educations[0].university_linkedin_url ?
                              match.candidate.educations[0].university_linkedin_url.split('/').pop() :
                              'University'}</span>
                          </>
                        )}
                        <span className="separator">•</span>
                        <span>{match.candidate?.location || 'Unknown location'}</span>
                      </div>
                      {match.created_at && (
                        <div className="match-date">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M12.5 7H11V13L16.2 16.2L17 14.9L12.5 12.2V7Z" fill="#8b5cf6" />
                          </svg>
                          <span>{formatMatchDate(match.created_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="match-score-badges">
                    <div className="match-score-badge pearch-score">
                      <span className="score-value">{match.pearch_score * 20}</span>
                      <span className="score-label">Pearch</span>
                    </div>
                    <div className="match-score-badge custom-score">
                      <span className="score-value">
                        {match.custom_score != null ? Math.round(match.custom_score) : '—'}
                      </span>
                      <span className="score-label">Custom</span>
                    </div>
                    <div className="confidence-badge">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#8b5cf6" />
                      </svg>
                      High Confidence
                    </div>
                  </div>
                </div>

                {/* Practice Area Tags */}
                {match.candidate?.expertise && match.candidate.expertise.length > 0 && (
                  <div className="practice-tags">
                    {match.candidate.expertise.slice(0, 3).map((skill, idx) => (
                      <span key={idx} className="practice-tag">{skill}</span>
                    ))}
                  </div>
                )}

                {/* Match Metrics */}
                <div className="match-metrics">
                  <div className="metric-item">
                    <div className="metric-header">
                      <span className="metric-label">Experience Match</span>
                      <span className={`metric-percentage ${getMatchPercentageClass(match.experience_match)}`}>
                        {match.experience_match}%
                      </span>
                    </div>
                    <div className="metric-bar">
                      <div
                        className={`metric-fill ${getMatchPercentageClass(match.experience_match)}`}
                        style={{ width: `${match.experience_match}%` }}
                      />
                    </div>
                  </div>

                  <div className="metric-item">
                    <div className="metric-header">
                      <span className="metric-label">Skills Alignment</span>
                      <span className={`metric-percentage ${getMatchPercentageClass(match.skills_alignment)}`}>
                        {match.skills_alignment}%
                      </span>
                    </div>
                    <div className="metric-bar">
                      <div
                        className={`metric-fill ${getMatchPercentageClass(match.skills_alignment)}`}
                        style={{ width: `${match.skills_alignment}%` }}
                      />
                    </div>
                  </div>

                  <div className="metric-item">
                    <div className="metric-header">
                      <span className="metric-label">Cultural Fit</span>
                      <span className={`metric-percentage ${getMatchPercentageClass(match.cultural_fit)}`}>
                        {match.cultural_fit}%
                      </span>
                    </div>
                    <div className="metric-bar">
                      <div
                        className={`metric-fill ${getMatchPercentageClass(match.cultural_fit)}`}
                        style={{ width: `${match.cultural_fit}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Feedback Buttons */}
                <div className="match-feedback">
                  <button
                    className={`feedback-btn feedback-btn--up ${match.feedback === 'thumbs_up' ? 'active' : ''}`}
                    onClick={() => handleFeedback(match.id, 'thumbs_up')}
                    disabled={!!match.feedback}
                    title={match.feedback ? 'Feedback already submitted — reset to change' : 'Good match'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" fill="currentColor" />
                    </svg>
                    Good match
                  </button>
                  <button
                    className={`feedback-btn feedback-btn--down ${match.feedback === 'thumbs_down' ? 'active' : ''}`}
                    onClick={() => handleThumbsDown(match.id)}
                    disabled={!!match.feedback}
                    title={match.feedback ? 'Feedback already submitted — reset to change' : 'Poor match'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" fill="currentColor" />
                    </svg>
                    Poor match
                  </button>
                  {match.feedback && (
                    <button
                      className="feedback-btn feedback-btn--reset"
                      onClick={() => handleResetFeedback(match.id)}
                      title="Reset feedback"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" fill="currentColor"/>
                      </svg>
                      Reset
                    </button>
                  )}
                </div>

                {/* Action Button */}
                <button
                  className="btn-view-details"
                  onClick={() => handleViewDetails(match)}
                >
                  View Details
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Candidate Detail Modal */}
      {showModal && selectedMatch && (
        <CandidateProfileModal
          match={selectedMatch}
          candidate={selectedMatch.candidate}
          job={job}
          onClose={handleCloseModal}
        />
      )}

      {/* Job Details Modal */}
      {showJobModal && jobId && (
        <JobDetailsModal
          jobId={jobId}
          onClose={() => setShowJobModal(false)}
        />
      )}

      {/* Thumbs Down Reason Modal */}
      {thumbsDownModal.open && (
        <div className="feedback-modal-overlay" onClick={() => setThumbsDownModal({ open: false, matchId: null })}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <div className="feedback-modal-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" fill="#ef4444" />
                </svg>
              </div>
              <h3 className="feedback-modal-title">Why is this a poor match?</h3>
              <p className="feedback-modal-subtitle">Your feedback helps improve future candidate ranking.</p>
            </div>
            <textarea
              className="feedback-modal-textarea"
              placeholder="e.g. Missing required practice area, insufficient experience, location mismatch..."
              value={thumbsDownReason}
              onChange={(e) => setThumbsDownReason(e.target.value)}
              rows={4}
              autoFocus
            />
            <div className="feedback-modal-actions">
              <button className="feedback-modal-btn feedback-modal-btn--skip" onClick={handleThumbsDownSkip}>
                Skip
              </button>
              <button className="feedback-modal-btn feedback-modal-btn--confirm" onClick={handleThumbsDownConfirm}>
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Matchmaker;
